import type { AxiosInstance } from 'axios';
import type { GenerateReferenceMode } from '@subscriptions/domain/types/SubscriptionsConst';
import { SUBSCRIPTION_BLOCK_ENDPOINTS, STORE_ENDPOINTS } from './endpoints';
import type {
  ApiEnvelope,
  SubscriptionBlockApiResponse,
  CreateSubscriptionBlockApiRequest,
  UpdateBlockPaymentMethodApiRequest,
  UpdateBlockInvoiceDataApiRequest,
  UpdateBlockStoresApiRequest,
  UpdateBlockStoresApiResponse,
  StoreApiResponse,
  PaymentMethodApiResponse,
  AttachPaymentMethodApiRequest,
} from './types';

/** HTTP client type for subscription block operations */
export type SubscriptionBlockHttpClientType = {
  list: () => Promise<SubscriptionBlockApiResponse[]>;
  getById: (blockUuid: string) => Promise<SubscriptionBlockApiResponse>;
  create: (request: CreateSubscriptionBlockApiRequest) => Promise<SubscriptionBlockApiResponse>;
  updatePaymentMethod: (
    blockUuid: string,
    request: UpdateBlockPaymentMethodApiRequest,
  ) => Promise<SubscriptionBlockApiResponse>;
  updateInvoiceData: (
    blockUuid: string,
    request: UpdateBlockInvoiceDataApiRequest,
  ) => Promise<SubscriptionBlockApiResponse>;
  updateStores: (blockUuid: string, request: UpdateBlockStoresApiRequest) => Promise<UpdateBlockStoresApiResponse>;
  delete: (blockUuid: string) => Promise<void>;
  listStores: (blockUuid?: string) => Promise<StoreApiResponse[]>;
  generateReference: (blockUuid: string, mode?: GenerateReferenceMode) => Promise<PaymentMethodApiResponse>;
  attachPaymentMethod: (blockUuid: string, paymentMethodUuid: string) => Promise<PaymentMethodApiResponse>;
};

/**
 * Creates the HTTP client for subscription block operations.
 * All routes target the parrot-subscriptions-api and unwrap the standard
 * `{data, datetime}` envelope.
 *
 * @param props.httpClientSubscription - Axios instance bound to parrot-subscriptions-api
 */
export const createSubscriptionBlockHttpClient = (props: {
  httpClientSubscription: AxiosInstance;
}): SubscriptionBlockHttpClientType => {
  const { httpClientSubscription } = props;

  const list = async (): Promise<SubscriptionBlockApiResponse[]> => {
    const response = await httpClientSubscription.get<ApiEnvelope<SubscriptionBlockApiResponse[]>>(
      SUBSCRIPTION_BLOCK_ENDPOINTS.BASE,
    );
    return response.data.data;
  };

  const getById = async (blockUuid: string): Promise<SubscriptionBlockApiResponse> => {
    const response = await httpClientSubscription.get<ApiEnvelope<SubscriptionBlockApiResponse>>(
      SUBSCRIPTION_BLOCK_ENDPOINTS.BY_ID(blockUuid),
    );
    return response.data.data;
  };

  const create = async (request: CreateSubscriptionBlockApiRequest): Promise<SubscriptionBlockApiResponse> => {
    const response = await httpClientSubscription.post<ApiEnvelope<SubscriptionBlockApiResponse>>(
      SUBSCRIPTION_BLOCK_ENDPOINTS.BASE,
      request,
    );
    return response.data.data;
  };

  const updatePaymentMethod = async (
    blockUuid: string,
    request: UpdateBlockPaymentMethodApiRequest,
  ): Promise<SubscriptionBlockApiResponse> => {
    const response = await httpClientSubscription.patch<ApiEnvelope<SubscriptionBlockApiResponse>>(
      SUBSCRIPTION_BLOCK_ENDPOINTS.BY_ID(blockUuid),
      request,
    );
    return response.data.data;
  };

  const updateInvoiceData = async (
    blockUuid: string,
    request: UpdateBlockInvoiceDataApiRequest,
  ): Promise<SubscriptionBlockApiResponse> => {
    const response = await httpClientSubscription.patch<ApiEnvelope<SubscriptionBlockApiResponse>>(
      SUBSCRIPTION_BLOCK_ENDPOINTS.BY_ID(blockUuid),
      request,
    );
    return response.data.data;
  };

  const updateStores = async (
    blockUuid: string,
    request: UpdateBlockStoresApiRequest,
  ): Promise<UpdateBlockStoresApiResponse> => {
    const response = await httpClientSubscription.patch<ApiEnvelope<UpdateBlockStoresApiResponse>>(
      SUBSCRIPTION_BLOCK_ENDPOINTS.BY_ID(blockUuid),
      request,
    );
    return { uuid: response.data.data.uuid };
  };

  const deleteFn = async (blockUuid: string): Promise<void> => {
    await httpClientSubscription.delete(SUBSCRIPTION_BLOCK_ENDPOINTS.BY_ID(blockUuid));
  };

  const listStores = async (blockUuid?: string): Promise<StoreApiResponse[]> => {
    const response = await httpClientSubscription.get<ApiEnvelope<StoreApiResponse[]>>(STORE_ENDPOINTS.LIST(blockUuid));
    return response.data.data;
  };

  const generateReference = async (
    blockUuid: string,
    mode?: GenerateReferenceMode,
  ): Promise<PaymentMethodApiResponse> => {
    const response = await httpClientSubscription.post<ApiEnvelope<PaymentMethodApiResponse>>(
      SUBSCRIPTION_BLOCK_ENDPOINTS.GENERATE_REFERENCE(blockUuid),
      undefined,
      mode ? { params: { mode } } : undefined,
    );
    return response.data.data;
  };

  const attachPaymentMethod = async (
    blockUuid: string,
    paymentMethodUuid: string,
  ): Promise<PaymentMethodApiResponse> => {
    const body: AttachPaymentMethodApiRequest = { paymentMethodUuid };
    const response = await httpClientSubscription.post<ApiEnvelope<PaymentMethodApiResponse>>(
      SUBSCRIPTION_BLOCK_ENDPOINTS.ATTACH_PAYMENT_METHOD(blockUuid),
      body,
    );
    return response.data.data;
  };

  return {
    list,
    getById,
    create,
    updatePaymentMethod,
    updateInvoiceData,
    updateStores,
    delete: deleteFn,
    listStores,
    generateReference,
    attachPaymentMethod,
  };
};
